import { Directive, DestroyRef, HostListener, inject, Signal, Type, WritableSignal } from '@angular/core';
import { filter, merge, of, switchMap } from 'rxjs';
import { DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AlarisDialogService, AlarisEditPanelService } from '@campaign-portal/components-library';
import { CanDeactivateGuardService } from './';

@Directive()
export abstract class CanDeactivateComponent {
	abstract allowedDeactivation: Signal<boolean>;

	readonly destroyRef = inject(DestroyRef);

	@HostListener('window:beforeunload', ['$event'])
	unloadNotification($event: BeforeUnloadEvent): void {
		if (!this.canDeactivate()) {
			$event.preventDefault();
		}
	}

	canDeactivate(): boolean {
		return this.allowedDeactivation();
	}

	addEditPanelGuard(
		editPanel: AlarisEditPanelService,
		guard: CanDeactivateGuardService,
		component?: Type<unknown>
	): void {
		editPanel.canBeClosed$ = this.allowedDeactivation;
		merge(editPanel.closingComponent$, editPanel.escKeyInComponent$, editPanel.backdropClickComponent$)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap(() => {
					return editPanel.canBeClosed ? of(true) : guard.lostDataDialog(component);
				})
			)
			.subscribe((result) => {
				(this.allowedDeactivation as WritableSignal<boolean>).set(result);
				editPanel.close();
			});
	}

	addDialogGuard(dialog: AlarisDialogService, dialogRef: DialogRef, guard: CanDeactivateGuardService): void {
		let manualClosingResult: unknown;
		dialog.canBeClosed$ = this.allowedDeactivation;
		merge(
			dialogRef.backdropClick,
			dialogRef.keydownEvents.pipe(filter((value: KeyboardEvent) => value.code === 'Escape')),
			dialog.manualClosing$.pipe(
				filter((closingDialog) => {
					if (closingDialog.ref === dialogRef) {
						manualClosingResult = closingDialog.result;
						return true;
					}
					return false;
				})
			)
		)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap(() => {
					return dialog.canBeClosed ? of(true) : guard.lostDataDialog();
				})
			)
			.subscribe((canDeactivate) => {
				(this.allowedDeactivation as WritableSignal<boolean>).set(canDeactivate);
				if (canDeactivate) {
					guard.closeLostDataDialog();
					dialog.defaultClose.call(dialogRef, manualClosingResult);
					manualClosingResult = undefined;
				}
			});
	}
}
